import { useEffect, useState } from 'react';
import AlertDialog from '@/components/ui/alert-dialog.jsx';
import { previewFacturaAfipRecovery, recoverFacturaFromAfip } from '@/services/facturacion.js';

const money = (value) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(value || 0));
const date = (value) => (value ? String(value).slice(0, 10).split('-').reverse().join('/') : '—');

// Two steps behind one button: query AFIP, and only when the draft matches, recover it.
export default function RecuperarFacturaAfipDialog({ open, onOpenChange, facturaId, tenant, token, onRecovered }) {
  const [numero, setNumero] = useState('');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) { setNumero(''); setPreview(null); setError(''); }
  }, [open]);

  const canRecover = Boolean(preview?.ok && preview.queriedNumber === Number(numero));

  const handleConfirm = async () => {
    setError('');
    const number = Number(numero);
    if (!Number.isInteger(number) || number <= 0) {
      setError('Indicá el número de comprobante que autorizó AFIP.');
      return false;
    }
    try {
      if (!canRecover) {
        const result = await previewFacturaAfipRecovery({ tenant, token, id: facturaId, numero: number });
        setPreview({ ...result, queriedNumber: number });
        return false;
      }
      const recovered = await recoverFacturaFromAfip({ tenant, token, id: facturaId, numero: number });
      onRecovered?.(recovered);
      return true;
    } catch (requestError) {
      const mismatches = requestError?.payload?.mismatches;
      if (mismatches?.length) setPreview((current) => ({ ...(current || {}), ok: false, mismatches, queriedNumber: number }));
      setError(requestError?.message || 'No se pudo consultar AFIP.');
      return false;
    }
  };

  const afip = preview?.afip;
  return (
    <AlertDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Recuperar factura desde AFIP"
      description="Para borradores que AFIP autorizó pero no se guardaron. Se consulta el comprobante en AFIP y, si coincide con el borrador, se registra como emitido con su CAE."
      confirmLabel={canRecover ? 'Recuperar factura' : 'Consultar AFIP'}
      pendingLabel={canRecover ? 'Recuperando...' : 'Consultando...'}
      onConfirm={handleConfirm}
    >
      <label className="block text-sm font-medium" htmlFor="recuperar-afip-numero">Número de comprobante</label>
      <input
        id="recuperar-afip-numero"
        inputMode="numeric"
        value={numero}
        onChange={(event) => { setNumero(event.target.value.replace(/\D/g, '')); setPreview(null); setError(''); }}
        placeholder="Ej. 2276"
        className="mt-1 w-full rounded-lg border border-[rgba(var(--border),.7)] bg-transparent p-2"
      />
      {afip && preview?.queriedNumber === Number(numero) ? (
        <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-[rgba(var(--muted),.96)]">Número</dt><dd>{preview.recuperado?.numero}</dd>
          <dt className="text-[rgba(var(--muted),.96)]">CAE</dt><dd>{afip.cae}</dd>
          <dt className="text-[rgba(var(--muted),.96)]">Vto. CAE</dt><dd>{date(afip.caeVencimiento)}</dd>
          <dt className="text-[rgba(var(--muted),.96)]">Fecha</dt><dd>{date(afip.cbteFch)}</dd>
          <dt className="text-[rgba(var(--muted),.96)]">CUIT receptor</dt><dd>{afip.docNro}</dd>
          <dt className="text-[rgba(var(--muted),.96)]">Total AFIP</dt><dd>{money(afip.impTotal)}</dd>
        </dl>
      ) : null}
      {preview?.mismatches?.length ? (
        <ul className="mt-4 list-disc space-y-1 pl-5 text-sm text-red-700 dark:text-red-300">
          {preview.mismatches.map((item) => <li key={item}>{item}</li>)}
        </ul>
      ) : null}
      {error ? <p role="alert" className="mt-3 text-sm text-red-700 dark:text-red-300">{error}</p> : null}
    </AlertDialog>
  );
}
